import { useUnleashClient } from '@unleash/proxy-client-react'
import { useCallback, useSyncExternalStore } from 'react'

import { isUnleashConfigured } from './config'

/**
 * True once Unleash has actually answered from the server (or failed) — unlike
 * `useFeatureFlagsReady`, which is already satisfied by the bootstrap the client
 * serves synchronously on init (it calls `setReady()` from the bootstrap before
 * any network fetch).
 *
 * Gate REDIRECT decisions on this hook, not on `useFeatureFlagsReady`. The
 * bootstrap is OFF / fail-closed for every flag, so a route guard that navigates
 * on the pre-fetch value would bounce a session away from a feature that is in
 * fact enabled for its environment, in the brief window before the real value
 * arrives over the wire.
 *
 * Resolves immediately when the client is unconfigured or has already fetched
 * this session; otherwise it waits for the client's first `update` (a
 * successful fetch stores the server toggles and emits it) or `error` (outage →
 * stop waiting and decide on the fallback).
 *
 * Built on `useSyncExternalStore` rather than an effect + `useState`: the fetch
 * can land between render and subscription, and the store re-reads its snapshot
 * right after subscribing. An effect would miss that event and hold the route's
 * loader forever.
 */
type WithFetchState = { fetchedFromServer?: boolean }

const hasFetched = (client: unknown): boolean =>
  Boolean((client as WithFetchState | undefined)?.fetchedFromServer)

const noop = () => () => {}

export const useFeatureFlagsServerSettled = (): boolean => {
  const client = useUnleashClient()

  const subscribe = useCallback(
    (onSettled: () => void) => {
      if (!isUnleashConfigured || !client) {
        return noop()
      }

      client.on('update', onSettled)
      client.on('error', onSettled)

      return () => {
        client.off('update', onSettled)
        client.off('error', onSettled)
      }
    },
    [client]
  )

  const getSnapshot = useCallback(
    () => !isUnleashConfigured || !client || hasFetched(client),
    [client]
  )

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
